import {AbstractLitElement} from "../../abstract-lit.element";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {Project} from "../../../domain/entities/project.entity";
import {container} from "tsyringe";
import {Issue} from "../../../domain/entities/issue.entity";
import {html, PropertyValues} from "lit";
import {IssueRepository} from "../../../domain/repositories/issue.repository";
import {IssueTypeRepository} from "../../../domain/repositories/issue-type.repository";
import {ISSUE_TYPE_MILESTONE} from "../../../constants";
import {DateHelper} from "../../../helpers/date.helper";
import {Interval} from "luxon";
import {ProxyService} from "../../../advanced-form/proxy.service";
import {FieldProxy} from "../../../advanced-form/field.proxy";
import {FormProxy} from "../../../advanced-form/form.proxy";
import {filter, takeUntil} from "rxjs";
import {EntityObjectArray} from "../../../domain/entity-object-array";

@customElement(`ha-plugin-timeframes`)
export class HaPluginTimeframesComponent extends AbstractLitElement {

    @property({type: Object})
    public component!: AbstractLitElement;
    private _proxyService: ProxyService = container.resolve(ProxyService);
    private _issueRepository = container.resolve(IssueRepository);
    private _issueTypeRepository = container.resolve(IssueTypeRepository);
    private _timeFrameIssues: EntityObjectArray<Issue> | null = null;
    private _formProxy: FormProxy<any> | null = null;

    private _entity: Project | null = null;

    @property({type: Object})
    public get entity(): Project | null {
        return this._entity;
    }

    public set entity(entity: Project | null) {
        if (!(entity instanceof Project && this._entity !== entity)) {
            return;
        }

        const oldValue = this._entity;
        this._entity = entity;

        Promise.all<Promise<any>>([
            this._proxyService.get(this.entity!.uid)!,
            this._initTimeFrameIssues(entity)
        ]).then(
            ([form]) => {
                this._formProxy = form;
                this.requestUpdate(`entity`, oldValue)
            }
        );
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this._formProxy?.removeField(`timeFrames`);
    }

    protected shouldUpdate(changedProperties: PropertyValues): boolean {
        return !!this._timeFrameIssues && super.shouldUpdate(changedProperties);
    }

    protected willUpdate(changedProperties: PropertyValues) {
        if (changedProperties.has(`entity`) || changedProperties.has(`component`) && !!this.component && !!this.entity) {
            this._formProxy!.changedValue$.pipe(
                takeUntil(this._disconnected$),
                filter(value => Object.keys(value).includes(`type`))
            ).subscribe(() => this.component.requestUpdate());
        }
    }

    protected render(): unknown {
        const bindTo = this._formProxy?.bindToFn!;

        return html`
            <ha-input-timeframes ${bindTo(`timeFrames`)}
                                 .board=${this.entity}
            ></ha-input-timeframes>
        `;
    }

    // private _registerForEntityEvents(entity: Project): void {
    //
    //     this._formProxy = this._proxyService.get(entity.uid);
    //     if (!this._formProxy) {
    //         return;
    //     }
    //
    //     if (!this._formProxy.hasField(`timeFrames`)) {
    //         this._formProxy.addField(
    //             `timeFrames`,
    //             new FieldProxy<Issue[]>(this._timeFrameIssues ?? [], container.resolve(`comparator.default`))
    //         );
    //     }
    // }

    private async _initTimeFrameIssues(entity: Project): Promise<void> {

        this._timeFrameIssues = entity.isNew()
            ? EntityObjectArray.fromArray([await this._createNewTimeFrameIssue(entity)])
            : await this._issueRepository.findAllByProjectUid(entity.uid);

        if (!this._formProxy) {
            console.warn(`no form proxy found`);
            return;
        }

        if (this._formProxy.hasField(`timeFrames`)) {
            throw new Error(`time frames already exists.`);
        }

        this._formProxy.addField(`timeFrames`, new FieldProxy<Issue[]>(
            `timeFrames`,
            entity.isNew() ? [] : this._timeFrameIssues,
            container.resolve(`comparator.default`)
        ));

        if (entity.isNew()) {
            this._formProxy.getField(`timeFrames`)!.value = this._timeFrameIssues;
        }
    }

    private async _createNewTimeFrameIssue(entity: Project): Promise<Issue> {
        const timeFrameIssue = await this._issueRepository.create();
        const latestDateTime = DateHelper.latest((this._timeFrameIssues ?? []).map(issue => issue.end)) ?? DateHelper.today;

        timeFrameIssue.timeRange = Interval.fromDateTimes(latestDateTime, latestDateTime.plus({day: 1}));
        timeFrameIssue.project = entity;
        timeFrameIssue.title = `Zeitfenster`;
        timeFrameIssue.type = (await this._issueTypeRepository.findOneById(ISSUE_TYPE_MILESTONE))!;

        return timeFrameIssue;
    }
}